import { useCallback } from 'react';
import { css } from '@emotion/react';
import type { TeamChatTypes } from '@frontend/api-team-chat';
import { highlightSegment } from '@frontend/components';
import { useTeamChatSelector } from '../../../providers/team-chat.provider';

type Props = {
  label: string;
  isSelf: boolean;
  highlightedText?: string;
  mentionUser?: TeamChatTypes.User;
};
export const MentionTag = ({ label, isSelf, highlightedText, mentionUser }: Props) => {
  const conversations = useTeamChatSelector((ctx) => ctx.conversations);
  const currentUser = useTeamChatSelector((ctx) => ctx.user);
  const openStaticConversation = useTeamChatSelector((ctx) => ctx.openStaticConversation);
  const createConversation = useTeamChatSelector((ctx) => ctx.createConversation);

  const onClick = useCallback(() => {
    if (!currentUser) {
      return null;
    }

    if (!mentionUser || mentionUser?.userID === currentUser?.userID || mentionUser?.isDeactivated) {
      return null;
    }

    const conversation = conversations?.dm.find((c) => {
      return c.memberIds.length === 1 && c.memberIds[0] === mentionUser.userID;
    });

    if (conversation) {
      return openStaticConversation(conversation.channelId);
    } else {
      return createConversation({
        memberIds: [mentionUser.userID],
        type: 'dm',
        name: undefined,
      });
    }
  }, [currentUser, mentionUser, conversations?.dm, createConversation, openStaticConversation]);

  return (
    <span
      css={[style, mentionUser?.isDeactivated && deactivatedStyle]}
      className={isSelf ? 'mention-self' : 'mention'}
      onClick={onClick}
    >
      {highlightedText ? highlightSegment(label, highlightedText) : label}
    </span>
  );
};

const style = css`
  background: var(--fabric-color-blue-5);
  border-radius: var(--fabric-radius-sm);
  color: var(--fabric-color-blue-60);
  cursor: pointer;
  padding: 0px 2px;

  &:hover {
    background: var(--fabric-color-blue-10);
    color: var(--fabric-color-blue-60);
  }

  &.mention-self {
    background: var(--fabric-color-yellow-5);
  }
`;

const deactivatedStyle = css`
  background: var(--fabric-color-neutral-10);
  border-radius: var(--fabric-radius-sm);
  color: var(--fabric-color-neutral-60);

  &:hover {
    background: var(--fabric-color-neutral-10);
    color: var(--fabric-color-neutral-60);
  }
`;
